import { useState, useEffect, useCallback } from 'react';
import { timeclockAPI } from '../lib/api';
import { toast } from 'sonner';
import { Loader2, Delete } from 'lucide-react';

export function PinLockScreen({ storeId, onAuthenticated }) {
    const [pin, setPin] = useState('');
    const [loading, setLoading] = useState(false);
    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        const interval = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(interval);
    }, []);

    const handleDigit = useCallback((digit) => {
        if (pin.length < 4) setPin(prev => prev + digit);
    }, [pin]);

    const handleClear = useCallback(() => setPin(''), []);
    const handleBackspace = useCallback(() => setPin(prev => prev.slice(0, -1)), []);

    const handleSubmit = useCallback(async () => {
        if (pin.length !== 4) return;
        setLoading(true);
        try {
            const res = await timeclockAPI.clockIn({ pin, store_id: storeId });
            const data = res.data;
            // Store token and user info
            localStorage.setItem('token', data.access_token);
            toast.success(data.message);
            onAuthenticated({
                token: data.access_token,
                userId: data.user_id,
                userName: data.user_name,
                userRole: data.user_role,
                entryId: data.id,
                clockIn: data.clock_in
            });
        } catch (err) {
            const detail = err.response?.data?.detail || 'Clock-in failed';
            toast.error(detail);
            setPin('');
        } finally {
            setLoading(false);
        }
    }, [pin, storeId, onAuthenticated]);

    useEffect(() => {
        if (pin.length === 4) handleSubmit();
    }, [pin, handleSubmit]);

    const digits = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

    return (
        <div className="h-screen flex flex-col items-center justify-center bg-zinc-50 dark:bg-zinc-950" data-testid="pin-lock-screen">
            {/* Logo */}
            <div className="mb-8 text-center">
                <div className="w-16 h-16 bg-zinc-200 dark:bg-zinc-800 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <span className="text-zinc-700 dark:text-zinc-200 font-bold text-2xl">M</span>
                </div>
                <h1 className="text-xl font-semibold tracking-tight">Mezgeb POS</h1>
                <p className="text-sm text-zinc-500 mt-1">Enter your PIN to clock in</p>
            </div>

            {/* PIN Dots */}
            <div className="flex gap-3 mb-8" data-testid="pin-dots">
                {[0, 1, 2, 3].map(i => (
                    <div
                        key={i}
                        className={`w-4 h-4 rounded-full transition-all duration-200 ${
                            i < pin.length
                                ? 'bg-zinc-900 dark:bg-white scale-110'
                                : 'bg-zinc-300 dark:bg-zinc-700'
                        }`}
                    />
                ))}
            </div>

            {/* Keypad */}
            <div className="grid grid-cols-3 gap-3 mb-6" data-testid="pin-keypad">
                {digits.map(d => (
                    <button
                        key={d}
                        onClick={() => handleDigit(d)}
                        disabled={loading}
                        className="w-20 h-20 rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 text-2xl font-semibold text-zinc-800 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 active:scale-95 transition-all shadow-sm"
                        data-testid={`pin-key-${d}`}
                    >
                        {d}
                    </button>
                ))}
                <button
                    onClick={handleBackspace}
                    disabled={loading}
                    className="w-20 h-20 rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 flex items-center justify-center text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 active:scale-95 transition-all shadow-sm"
                    data-testid="pin-key-backspace"
                >
                    <Delete className="h-6 w-6" />
                </button>
                <button
                    onClick={() => handleDigit('0')}
                    disabled={loading}
                    className="w-20 h-20 rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 text-2xl font-semibold text-zinc-800 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 active:scale-95 transition-all shadow-sm"
                    data-testid="pin-key-0"
                >
                    0
                </button>
                <button
                    onClick={handleClear}
                    disabled={loading}
                    className="w-20 h-20 rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 text-sm font-medium text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 active:scale-95 transition-all shadow-sm"
                    data-testid="pin-key-clear"
                >
                    Clear
                </button>
            </div>

            {loading && (
                <div className="flex items-center gap-2 text-zinc-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Clocking in...</span>
                </div>
            )}

            {/* Time Display */}
            <div className="mt-6 text-center text-zinc-400">
                <p className="text-lg font-mono">{currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                <p className="text-sm">{currentTime.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
            </div>
        </div>
    );
}
